// apps/admin/src/components/monitoring/EmergencyRebuildPanel.tsx
import React, { useState } from 'react';
import { ValidationService } from '../../services/ValidationService';
import type { ValidationStatus } from '../../types/validation';
import { AlertTriangle, RefreshCw, X, CheckCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useAdminAuth } from '../../contexts/AdminContext';

interface EmergencyRebuildPanelProps {
  statuses: ValidationStatus[];
  onRebuildComplete: () => void;
}

export function EmergencyRebuildPanel({ statuses, onRebuildComplete }: EmergencyRebuildPanelProps) {
  const { user } = useAdminAuth();
  const [selectedCompany, setSelectedCompany] = useState<string>('');
  const [reason, setReason] = useState<string>('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [rebuilding, setRebuilding] = useState(false);

  const handleRebuildClick = () => {
    if (!selectedCompany) {
      toast.error('Please select a company');
      return;
    }
    if (!reason.trim()) {
      toast.error('Please provide a reason for the rebuild');
      return;
    }
    setShowConfirmModal(true);
  };

  const handleConfirmRebuild = async () => {
    if (!selectedCompany || !user?.email) return;

    setRebuilding(true);
    setShowConfirmModal(false);

    const selectedStatus = statuses.find(s => s.companyId === selectedCompany);
    const companyName = selectedStatus?.companyName || selectedCompany;

    try {
      toast.loading(`Rebuilding aggregates for ${companyName}...`, { id: 'rebuild' });

      const result = await ValidationService.triggerManualRebuild({
        companyId: selectedCompany,
        triggeredBy: user.email,
        reason: reason.trim()
      });

      toast.success(
        `✅ Rebuild complete for ${companyName} in ${(result.duration / 1000).toFixed(2)}s`,
        { id: 'rebuild', duration: 5000 }
      );

      // Reset form
      setSelectedCompany('');
      setReason('');

      // Refresh parent data
      onRebuildComplete();
    } catch (error: any) {
  // console.error('Rebuild error:', error);
      toast.error(`Failed to rebuild: ${error.message}`, { id: 'rebuild' });
    } finally {
      setRebuilding(false);
    }
  };

  const getCompanyStatusColor = (status: string) => {
    switch (status) {
      case 'error':
        return 'text-red-400';
      case 'warning':
        return 'text-yellow-400';
      case 'rebuilding':
        return 'text-blue-400';
      default:
        return 'text-slate-400';
    }
  };

  // Filter companies that might need attention (error, warning, or rebuilding)
  const companiesNeedingAttention = statuses.filter(
    s => s.status === 'error' || s.status === 'warning' || s.status === 'rebuilding'
  );

  return (
    <>
      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700/50 overflow-hidden">
        <div className="p-6 border-b border-slate-700/50 bg-gradient-to-r from-orange-900/20 to-red-900/20">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-500/20 rounded-lg">
              <AlertTriangle className="w-6 h-6 text-orange-400" />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-white">Emergency Rebuild</h3>
              <p className="text-sm text-slate-400 mt-1">
                Manually trigger aggregate rebuild for companies with validation errors
              </p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-4">
          {/* Warning Banner */}
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
            <div className="flex gap-3">
              <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-sm font-semibold text-yellow-400 mb-1">⚠️ Use with Caution</h4>
                <p className="text-xs text-yellow-400/80">
                  Manual rebuilds should only be used when automatic validation fails. 
                  This operation will recalculate all monthly aggregates from source data.
                </p>
              </div>
            </div>
          </div>

          {/* Companies Needing Attention */}
          {companiesNeedingAttention.length > 0 && (
            <div className="bg-slate-900/50 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-slate-300 mb-3">
                Companies Needing Attention ({companiesNeedingAttention.length})
              </h4>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {companiesNeedingAttention.map(status => (
                  <div
                    key={status.companyId}
                    className="flex items-center justify-between p-2 bg-slate-800/50 rounded hover:bg-slate-800 transition-colors"
                  >
                    <div>
                      <p className="text-sm text-white font-medium">{status.companyName}</p>
                      <p className="text-xs text-slate-400">
                        {status.monthsFailed} month{status.monthsFailed !== 1 ? 's' : ''} failed
                      </p>
                    </div>
                    <span className={`text-xs font-medium ${getCompanyStatusColor(status.status)}`}>
                      {status.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Rebuild Form */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Select Company *
              </label>
              <select
                value={selectedCompany}
                onChange={(e) => setSelectedCompany(e.target.value)}
                disabled={rebuilding}
                className="w-full px-4 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white focus:border-blue-500 focus:outline-none transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="">-- Select a company --</option>
                {statuses.map(status => (
                  <option key={status.companyId} value={status.companyId}>
                    {status.companyName} ({status.status})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Reason for Rebuild *
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                disabled={rebuilding}
                placeholder="e.g., Manual verification after data import, validation error investigation..."
                rows={3}
                className="w-full px-4 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none transition-colors disabled:opacity-50 disabled:cursor-not-allowed resize-none"
              />
              <p className="text-xs text-slate-400 mt-1">
                This will be logged for audit purposes
              </p>
            </div>

            <button
              onClick={handleRebuildClick}
              disabled={rebuilding || !selectedCompany || !reason.trim()}
              className="w-full px-4 py-3 bg-gradient-to-r from-orange-600 to-red-600 text-white rounded-lg font-medium hover:from-orange-700 hover:to-red-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg"
            >
              {rebuilding ? (
                <>
                  <RefreshCw className="w-5 h-5 animate-spin" />
                  Rebuilding...
                </>
              ) : (
                <>
                  <RefreshCw className="w-5 h-5" />
                  Trigger Manual Rebuild
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="relative bg-slate-800/95 backdrop-blur-xl rounded-2xl w-full max-w-md border border-slate-700/50 shadow-2xl animate-slide-in">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-orange-500/20 rounded-lg">
                    <AlertTriangle className="w-6 h-6 text-orange-400" />
                  </div>
                  <h3 className="text-xl font-bold text-white">Confirm Rebuild</h3>
                </div>
                <button
                  onClick={() => setShowConfirmModal(false)}
                  className="p-2 rounded-lg hover:bg-slate-700/50 transition-colors"
                >
                  <X className="w-5 h-5 text-slate-400" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4">
                  <p className="text-sm text-orange-400">
                    You are about to rebuild aggregates for:
                  </p>
                  <p className="text-white font-semibold mt-2">
                    {statuses.find(s => s.companyId === selectedCompany)?.companyName}
                  </p>
                </div>

                <div className="bg-slate-900/50 rounded-lg p-4">
                  <p className="text-xs text-slate-400 mb-2">Reason:</p>
                  <p className="text-sm text-slate-300">{reason}</p>
                </div>

                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
                  <p className="text-xs text-yellow-400">
                    ⚠️ This operation may take several minutes and will recalculate all monthly aggregates.
                  </p>
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    onClick={() => setShowConfirmModal(false)}
                    className="flex-1 px-4 py-2 bg-slate-700/50 text-white rounded-lg hover:bg-slate-600/50 transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleConfirmRebuild}
                    className="flex-1 px-4 py-2 bg-gradient-to-r from-orange-600 to-red-600 text-white rounded-lg hover:from-orange-700 hover:to-red-700 transition-all flex items-center justify-center gap-2 shadow-lg"
                  >
                    <CheckCircle className="w-4 h-4" />
                    Confirm Rebuild
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}